import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { DocumentType } from "../types/Types";

type PartnerInfo = {
  id: string;
  name: string;
};

type PartnerLookup = Record<string, PartnerInfo>;

export const useMaterialDocumentPartners = (materialDocuments: DocumentType[]) => {
  const [partners, setPartners] = useState<PartnerLookup>({});
  const processedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let isMounted = true;

    const fetchPartnerInfo = async () => {
      if (!materialDocuments || materialDocuments.length === 0) {
        return;
      }

      const token = localStorage.getItem("token") ?? "";
      const apiUrl = process.env.REACT_APP_API_URL ?? "";
      const updates: PartnerLookup = {};

      for (const doc of materialDocuments) {
        if (!doc?._id || processedRef.current.has(doc._id)) {
          continue;
        }
        processedRef.current.add(doc._id);

        let partnerId = "";
        let partnerName = "";

        if (doc.warehouse && typeof doc.warehouse === "object") {
          partnerId = doc.warehouse._id || "";
          partnerName = doc.warehouse.name || doc.warehouse.code || "";
        } else if (typeof doc.warehouse === "string") {
          partnerId = doc.warehouse;
        }

        // TODO: batch these lookups into a single request to avoid N+1 requests per document type
        if ((!partnerId || !partnerName) && doc.code && token && apiUrl) {
          try {
            const response = await axios.get(`${apiUrl}/api/documents-material/by-type/${doc.code}`, {
              headers: { "x-auth-token": token },
            });

            const partner = response.data?.partner;
            partnerId = partner?._id || partnerId;
            partnerName = partner?.name || partner?.partner || partnerName;
          } catch {
            // silently skip — partner suggestion is optional
          }
        }

        if (partnerId || partnerName) {
          updates[doc._id] = {
            id: partnerId,
            name: partnerName,
          };
        }
      }

      if (isMounted && Object.keys(updates).length > 0) {
        setPartners((prev) => ({ ...prev, ...updates }));
      }
    };

    fetchPartnerInfo();

    return () => {
      isMounted = false;
    };
  }, [materialDocuments]);

  return partners;
};

